import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Lock,
  Zap,
  Server,
  X,
  Sparkles,
} from 'lucide-react';
import { passwordProblem, PASSWORD_MIN_LENGTH } from '@prakash-software/shared';
import { signup, type SignupFieldErrors } from '../lib/auth-api';
import { isApiError } from '../lib/api';
import { AnimatedContent, ClickSpark } from '../components/react-bits';
import styles from './Login.module.css';
import own from './Signup.module.css';

// Self-serve signup. Short on purpose: who you are, how you sign in, and
// consent. The website and workspace name are optional here and asked again
// in onboarding. The server is the authority on every rule; the checks below
// only save a round trip.

const TERMS_URL = import.meta.env.VITE_TERMS_URL as string | undefined;
const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL as string | undefined;

const MESSAGES: Record<string, Record<string, string>> = {
  name: { invalid: 'Enter your name.' },
  email: { invalid: 'Enter a valid email address.' },
  password: {
    'too-short': `Use at least ${PASSWORD_MIN_LENGTH} characters.`,
    'too-long': 'That password is too long.',
    blank: "A password can't be only spaces.",
    'same-as-email': "Don't use your email address as your password.",
    invalid: 'Choose a different password.',
  },
  website: {
    'invalid-website': 'Enter a website like example.com, or leave it blank.',
    invalid: 'Enter a website like example.com.',
  },
  workspaceName: { invalid: 'Use 200 characters or fewer.' },
  acceptTerms: { required: 'Please accept the terms to continue.' },
};
const messageFor = (field: string, code: string) =>
  MESSAGES[field]?.[code] ?? MESSAGES[field]?.['invalid'] ?? 'Check this field.';

export function Signup() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [website, setWebsite] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const local: SignupFieldErrors = {};
    const pw = passwordProblem(password, email);
    if (pw) local['password'] = pw;
    if (!acceptTerms) local['acceptTerms'] = 'required';
    setFieldErrors(local);
    if (Object.keys(local).length > 0) return;

    setSubmitting(true);
    try {
      await signup({
        name: name.trim(),
        email: email.trim(),
        password,
        acceptTerms,
        ...(workspaceName.trim() ? { workspaceName: workspaceName.trim() } : {}),
        ...(website.trim() ? { website: website.trim() } : {}),
      });
      // Only the address travels on; the password never leaves this form.
      navigate(`/verify-email?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setSubmitting(false);
      if (isApiError(err) && err.status === 400) {
        setFieldErrors(((err.body as { fields?: SignupFieldErrors }) ?? {}).fields ?? {});
      } else if (isApiError(err) && err.status === 429) {
        setError('Too many signups from this network. Try again in a little while.');
      } else {
        setError('Could not reach the server. Try again in a moment.');
      }
    }
  }

  const fieldError = (field: string) =>
    fieldErrors[field] ? (
      <p className={own.fieldError} id={`signup-${field}-error`} role="alert">
        {messageFor(field, fieldErrors[field]!)}
      </p>
    ) : null;
  const describedBy = (field: string) => (fieldErrors[field] ? `signup-${field}-error` : undefined);

  return (
    <ClickSpark sparkColor="#0979f5" sparkSize={10} sparkRadius={22} sparkCount={10}>
      <main className={styles.root}>
        {/* Studio Ambient Backdrop */}
        <div className={styles.studioGlow} aria-hidden="true" />

        {/* Studio Modal Card */}
        <AnimatedContent
          trigger="mount"
          distance={25}
          duration={0.65}
          ease="power3.out"
          className={styles.modalCard}
        >
          {/* ── Left Column: Form & Brand ── */}
          <div className={styles.formCol}>
            <div className={styles.topBrandRow}>
              <Link to="/" className={styles.brandPill} title="Openline Home">
                <span className={styles.brandPillDot} />
                <span>Openline</span>
              </Link>
              <div className={own.headerBadge}>
                <Sparkles size={11} />
                <span>SOVEREIGN WORKSPACE</span>
              </div>
            </div>

            <div className={styles.headerBlock}>
              <h1 className={styles.title}>Create your workspace</h1>
              <p className={styles.subtitle}>
                Deploy sovereign live chat, autonomous AI agent &amp; visitor telemetry in minutes.
              </p>
            </div>

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}

            <form className={own.signupForm} onSubmit={handleSubmit} noValidate>
              {/* Full Name */}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="signup-name">
                  Full Name
                </label>
                <input
                  id="signup-name"
                  className={styles.input}
                  autoComplete="name"
                  required
                  maxLength={100}
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (fieldErrors['name']) {
                      setFieldErrors((prev) => {
                        const copy = { ...prev };
                        delete copy['name'];
                        return copy;
                      });
                    }
                  }}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors['name']}
                  aria-describedby={describedBy('name')}
                />
                {fieldError('name')}
              </div>

              {/* Work Email */}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="signup-email">
                  Work Email
                </label>
                <input
                  id="signup-email"
                  className={styles.input}
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors['email']) {
                      setFieldErrors((prev) => {
                        const copy = { ...prev };
                        delete copy['email'];
                        return copy;
                      });
                    }
                  }}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors['email']}
                  aria-describedby={describedBy('email')}
                />
                {fieldError('email')}
              </div>

              {/* Password */}
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label} htmlFor="signup-password">
                    Password
                  </label>
                </div>
                <div className={styles.passwordWrap}>
                  <input
                    id="signup-password"
                    className={styles.input}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••••••"
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors['password']) {
                        setFieldErrors((prev) => {
                          const copy = { ...prev };
                          delete copy['password'];
                          return copy;
                        });
                      }
                    }}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors['password']}
                    aria-describedby={describedBy('password') ?? 'signup-password-hint'}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {fieldError('password') ?? (
                  <p className={own.fieldHint} id="signup-password-hint">
                    At least {PASSWORD_MIN_LENGTH} characters. Spaces are fine.
                  </p>
                )}
              </div>

              {/* 2-Col Row: Company Name & Website */}
              <div className={own.twoColRow}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="signup-workspace">
                    Company <span className={own.optional}>(optional)</span>
                  </label>
                  <input
                    id="signup-workspace"
                    className={styles.input}
                    autoComplete="organization"
                    maxLength={200}
                    placeholder="Acme Inc."
                    value={workspaceName}
                    onChange={(e) => {
                      setWorkspaceName(e.target.value);
                      if (fieldErrors['workspaceName']) {
                        setFieldErrors((prev) => {
                          const copy = { ...prev };
                          delete copy['workspaceName'];
                          return copy;
                        });
                      }
                    }}
                    disabled={submitting}
                    aria-describedby={describedBy('workspaceName')}
                  />
                  {fieldError('workspaceName')}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="signup-website">
                    Website <span className={own.optional}>(optional)</span>
                  </label>
                  <input
                    id="signup-website"
                    className={styles.input}
                    inputMode="url"
                    autoComplete="url"
                    placeholder="acme.com"
                    value={website}
                    onChange={(e) => {
                      setWebsite(e.target.value);
                      if (fieldErrors['website']) {
                        setFieldErrors((prev) => {
                          const copy = { ...prev };
                          delete copy['website'];
                          return copy;
                        });
                      }
                    }}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors['website']}
                    aria-describedby={describedBy('website')}
                  />
                  {fieldError('website')}
                </div>
              </div>

              {/* Terms and consent */}
              <div className={own.consentWrap}>
                <label className={own.consent} htmlFor="signup-terms">
                  <input
                    id="signup-terms"
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => {
                      setAcceptTerms(e.target.checked);
                      if (fieldErrors['acceptTerms']) {
                        setFieldErrors((prev) => {
                          const copy = { ...prev };
                          delete copy['acceptTerms'];
                          return copy;
                        });
                      }
                    }}
                    disabled={submitting}
                    aria-invalid={!!fieldErrors['acceptTerms']}
                    aria-describedby={describedBy('acceptTerms')}
                  />
                  <span>
                    I agree to the{' '}
                    {TERMS_URL ? (
                      <a href={TERMS_URL} target="_blank" rel="noreferrer" className={own.termsLink}>
                        Terms of Service
                      </a>
                    ) : (
                      'Terms of Service'
                    )}{' '}
                    and{' '}
                    {PRIVACY_URL ? (
                      <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className={own.termsLink}>
                        Privacy Policy
                      </a>
                    ) : (
                      'Privacy Policy'
                    )}
                    .
                  </span>
                </label>
                {fieldError('acceptTerms')}
              </div>

              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                <span>{submitting ? 'Creating workspace…' : 'Create sovereign workspace'}</span>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            </form>

            <div className={styles.footerRow}>
              <span className={own.footerNotice}>
                Already have an account?{' '}
                <Link to="/login" className={own.loginHighlightLink}>
                  Sign in
                </Link>
              </span>
              <Link to="/" className={styles.footerLink}>
                Back to home
              </Link>
            </div>
          </div>

          {/* ── Right Column: Sovereign Workstation Showcase ── */}
          <div className={styles.visualCol}>
            <Link to="/" className={styles.closeBtn} title="Back to home" aria-label="Close signup modal">
              <X size={18} />
            </Link>

            <div className={styles.showcaseHardwareFrame}>
              <div className={styles.showcaseTitlebar}>
                <div className={styles.showcaseDots}>
                  <span className={`${styles.windowDot} ${styles.dotRed}`} />
                  <span className={`${styles.windowDot} ${styles.dotYellow}`} />
                  <span className={`${styles.windowDot} ${styles.dotGreen}`} />
                </div>
                <div className={styles.showcaseAddress}>
                  <Lock size={11} className={styles.showcaseLockIcon} />
                  <span>openline.internal · workstation setup</span>
                </div>
                <span className={styles.showcaseTelemetry}>
                  <span className={styles.livePulseDot} /> &lt;12ms
                </span>
              </div>

              <div className={styles.showcaseImageWrap}>
                <img
                  src="/landing/Openline-agent-01.png"
                  alt="Openline sovereign live agent conversation workstation"
                  className={styles.showcaseImg}
                  loading="eager"
                />
              </div>
            </div>

            <div className={styles.showcaseBadges}>
              <div className={styles.showcaseBadgeItem}>
                <ShieldCheck size={14} className={styles.badgeIcon} />
                <span>Zero Cloud Telemetry &amp; Full Data Sovereignty</span>
              </div>
              <div className={styles.showcaseBadgeItem}>
                <Zap size={14} className={styles.badgeIcon} />
                <span>Omnichannel Inbox &amp; Autonomous AI Agent</span>
              </div>
              <div className={styles.showcaseBadgeItem}>
                <Server size={14} className={styles.badgeIcon} />
                <span>Self-Hosted &amp; Air-Gapped Infrastructure Ready</span>
              </div>
            </div>
          </div>
        </AnimatedContent>
      </main>
    </ClickSpark>
  );
}
